"use client";
import {OneToastContainer, toast} from "@/components/commons/one-toast-container";
import {useUser} from "@/components/commons/user-context";
import {Button} from "@/components/ui/button";
import {Form, FormControl, FormField, FormItem, FormLabel, FormMessage} from "@/components/ui/form";
import {Input} from "@/components/ui/input";
import {cn} from "@/lib/utils";
import {zodResolver} from "@hookform/resolvers/zod";
import Link from "next/link";
import {useRouter} from "next/navigation";
import {useState} from "react";
import {useForm} from "react-hook-form";
import {z} from "zod";
import {GoogleLoginButton} from "./google-login-button";
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from "@/components/ui/card";
import { paths } from "@/lib/paths";

const loginSchema = z.object({
  email: z.string().email({message: "Invalid email address"}),
  password: z.string().min(6, {message: "Password must be at least 6 characters"}),
});

export function LoginForm({className, ...props}: React.ComponentProps<"form">) {
  const [loading, setLoading] = useState(false);
  const router = useRouter();
  const {login: setUser} = useUser();

  const form = useForm<z.infer<typeof loginSchema>>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const onSubmit = async (values: z.infer<typeof loginSchema>) => {
    setLoading(true);
    try {
      const res = await fetch("/api/users/login", {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
          email: values.email,
          password: values.password,
        }),
      });
      const data = await res.json();
      if (res.ok && data.status === "success" && data.data) {
        handleLogin(data.data);
      } else {
        toast.error(data?.error || data?.message || "Login failed");
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  function handleLogin(loginData: any) {
    if (loginData?.tenants?.length > 0) {
      loginData.tenants[0].selected = true;
    }    
    setUser(loginData);
    router.push("/");
    if (loginData.tenants.length > 0) {
      localStorage.setItem("tenantId", loginData.tenants[0]?._id);
      router.push(paths.dashboard.root);
    } else {
      router.push("/onboarding");
    }
  }

  return (
    <div className={cn("flex flex-col gap-6", className)}>
      <OneToastContainer />

      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-xl">Login to your account</CardTitle>
          <CardDescription>Enter your email below to login to your account</CardDescription>
        </CardHeader>
        <CardContent className="pt-6 pl-6 pr-6 pb-6 w-full">
          <Form {...form}>
            <form className={cn("flex flex-col gap-6", className)} onSubmit={form.handleSubmit(onSubmit)} {...props}>
              {/* <div className="flex flex-col items-center gap-2 text-center">
                <h1 className="text-2xl font-bold">Login to your account</h1>
                <p className="text-muted-foreground text-sm text-balance">Enter your email below to login to your account</p>
              </div> */}
              <div className="grid gap-6">
                <FormField
                  control={form.control}
                  name="email"
                  render={({field}) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input placeholder="me@mycompany.com" type="email" autoComplete="email" {...field} disabled={loading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="password"
                  render={({field}) => (
                    <FormItem>
                      <div className="flex items-center">
                        <FormLabel>Password</FormLabel>
                        <Link href="/forgot-password" className="ml-auto text-sm underline-offset-4 hover:underline">
                          Forgot your password?
                        </Link>
                      </div>
                      <FormControl>
                        <Input type="password" autoComplete="current-password" {...field} disabled={loading} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <Button type="submit" className="w-full" disabled={loading}>
                  Login
                </Button>
              </div>
            </form>
          </Form>

          <div className="mt-6">
            <div className="after:border-border relative text-center text-sm after:absolute after:inset-0 after:top-1/2 after:z-0 after:flex after:items-center after:border-t">
              <span className="bg-card text-muted-foreground relative z-10 px-2">Or continue with</span>
            </div>
            <div className="mt-6">
              <GoogleLoginButton onLogin={x => handleLogin(x)} />
            </div>
            <div className="mt-6 text-center text-sm">
              Don&apos;t have an account?{" "}
              <Link href="/signup" className="underline underline-offset-4">
                Sign up
              </Link>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
